/**
 * Список декларацій платника єдиного податку (ч.1113) — за зразком
 * декларацій з ПДВ: InfiniteDocGrid + статус подання з Журналу Кабінету ДПС.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  PERIOD_LABELS, singleTaxDeclarations, type SingleTaxDeclaration,
} from '@/api/singleTaxDeclarations';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { fmtMoney } from './SingleTaxBlank';

const SUB_STATUS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', 'var(--muted)'],
  SIGNED: ['підписана', 'var(--badge-blue-fg)'],
  SENT: ['відправлена', 'var(--badge-blue-fg)'],
  DELIVERED: ['обробляється', 'var(--badge-blue-fg)'],
  ACCEPTED: ['прийнята', 'var(--badge-green-fg)'],
  REJECTED: ['не прийнята', 'var(--badge-red-fg)'],
  ERROR: ['помилка', 'var(--badge-red-fg)'],
};

/** «До сплати» для списку: ФОП — р.14, юрособа — р.12 (графа 3 або 4). */
export function dueAmount(d: SingleTaxDeclaration): string {
  const v = d.values ?? {};
  if (d.form_code === 'J0103509') return v.R012G3 ?? v.R012G4 ?? '0';
  return v.R014G3 ?? '0';
}

export default function SingleTaxDeclarationsPage() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('documents.single_tax_calculation.manage');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const [orgId, setOrgId] = useOrgFilter();
  const period = useDefaultPeriod();
  const [fYear, setFYear] = useState<string>(() => period.dateFrom ? period.dateFrom.slice(0, 4) : '');
  const [fQuarter, setFQuarter] = useState<string>('');
  const { datasource, total } = useInfiniteGridDatasource<SingleTaxDeclaration>(
    (limit, offset, sort) => singleTaxDeclarations.list({
      organization_id: orgId || undefined,
      period_year: fYear ? Number(fYear) : undefined,
      period_quarter: fQuarter ? Number(fQuarter) : undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, fYear, fQuarter],
  );
  const subsQ = useQuery({
    queryKey: ['single-tax-decl-subs'],
    queryFn: () => dpsSubmissions.list({ source_type: 'doc.single_tax_declarations', limit: 500 }),
  });
  const subByDecl = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subsQ.data?.items ?? []) {
      if (s.source_id && !m.has(s.source_id)) m.set(s.source_id, s.status);
    }
    return m;
  }, [subsQ.data]);

  const cols = useMemo<ColDef<SingleTaxDeclaration>[]>(() => [
    { colId: 'period', headerName: 'Період', width: 150,
      valueGetter: (p) => p.data ? `${PERIOD_LABELS[p.data.period_quarter]} ${p.data.period_year}` : '' },
    { field: 'form_code', headerName: 'Форма', width: 110 },
    { field: 'document_number', headerName: '№', width: 170 },
    { field: 'organization_id', headerName: 'Організація', width: 220,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '' },
    { colId: 'due', headerName: 'ЄП до сплати', width: 140, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? dueAmount(p.data) : null,
      valueFormatter: (p) => p.data ? fmtMoney(p.value) : '' },
    { colId: 'submission', headerName: 'Подання', width: 140, sortable: false,
      cellRenderer: (p: { data?: SingleTaxDeclaration }) => {
        if (!p.data) return '';
        const st = subByDecl.get(p.data.id);
        if (!st) return <span style={{ color: 'var(--muted)' }}>—</span>;
        const [txt, color] = SUB_STATUS[st] ?? [st, 'inherit'];
        return <span style={{ color, fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      } },
    { field: 'created_at', headerName: 'Створено', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '' },
  ], [subByDecl, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Декларації платника єдиного податку<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/taxation/single-tax-declarations/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій` : undefined}>
          + Створити за період
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={fQuarter} onChange={(e) => setFQuarter(e.target.value)} style={{ width: 150, flex: '0 0 auto' }}>
          <option value="">— період —</option>
          {[1, 2, 3, 4].map((q) => <option key={q} value={String(q)}>{PERIOD_LABELS[q]}</option>)}
        </select>
        <select value={fYear} onChange={(e) => setFYear(e.target.value)} style={{ width: 110, flex: '0 0 auto' }}>
          <option value="">— рік —</option>
          {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() + 1 - i).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<SingleTaxDeclaration>
        datasource={datasource}
        columnDefs={cols}
        storageKey="single-tax-declarations"
        onRowDoubleClicked={(r) => navigate(`/taxation/single-tax-declarations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
